import { useEditor } from "@smartfire/js-block-engine";
import { useMediaQuery, Skeleton } from "@mui/material";
import clsx from "clsx";
import he from "he";
import * as moment from "moment/moment";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { adjustColor, displayFormat } from "../../shared/tools";
import style from "./style.module.scss";

export const Post = ({
  post,
  whiteMobileVerbose = false,
  verboseMobile = false,
  displayCategory = true,
  overwriteColor = null,
  titleColor = "#F5F4EF",
}) => {
  const { isEditing } = useEditor();
  const [imageLoaded, setImageLoaded] = useState(false);
  const isDesktop = useMediaQuery("(min-width:" + style.mobileBreakpoint + ")");
  const isMobile = useMediaQuery("(max-width: " + style.mobileBreakpoint + ")");
  const TagName = isEditing ? "div" : "a";
  // Si pas d'image principale, prendre la miniature du post
  const mainImage = post.image || post.thumbnail;
  // React i18next
  const { t } = useTranslation();

  // Si overwriteColor est défini, remplacer les couleurs (utilisé pour le bloc "Post Selector - Landing Page")
  const blockStyles = overwriteColor
    ? {
        "--overwriteColor": overwriteColor,
        "--overwriteHoverColor": adjustColor(overwriteColor, 0.2),
      }
    : undefined;

  useEffect(() => {
    if (mainImage?.url) {
      const img = new Image();
      img.src = mainImage.url;
      img.onload = () => setImageLoaded(true);
      img.onerror = () => {
        setImageLoaded(true);
      };
    }
  }, [mainImage?.url]);

  return (
    <div
      className={clsx(style.container, verboseMobile && style.verboseMobile)}
      style={blockStyles}
    >
      <TagName
        href={!isEditing ? post.link : undefined}
        className={clsx(
          style.post,
          (!verboseMobile || isDesktop) && style.hoverPost
        )}
      >
        {!mainImage?.url ? (
          <div className={style.skeleton} />
        ) : (
          <div className={style.imageContainer}>
            {!imageLoaded && (
              <Skeleton
                variant="rectangular"
                className={clsx(style.skeletonImage, style.mainImage)}
              />
            )}
            <img
              className={clsx(style.mainImage, style.zoom)}
              alt={mainImage.alt || mainImage.title}
              src={mainImage.url}
              style={{ display: imageLoaded ? "block" : "none" }}
              loading="lazy"
            />
          </div>
        )}

        <div
          className={clsx(style.info, whiteMobileVerbose && style.whiteVerbose)}
        >
          {displayCategory && !isDesktop && verboseMobile && post.cat && (
            <p className={style.catVerbose}>{he.decode(post.cat)}</p>
          )}
          <h3
            className={style.title}
            style={{
              color:
                !isMobile ||
                (isMobile && whiteMobileVerbose) ||
                (isMobile && !verboseMobile)
                  ? titleColor
                  : "inherit",
            }}
          >
            {he.decode(post.title)}
          </h3>
          <div
            className={style.author}
            style={{
              color:
                !isMobile ||
                (isMobile && whiteMobileVerbose) ||
                (isMobile && !verboseMobile)
                  ? titleColor
                  : "inherit",
            }}
          >
            <b>{post.author}</b> {t("on")}{" "}
            {moment(post.date).format(displayFormat())}
          </div>
        </div>
      </TagName>

      {(isDesktop || !verboseMobile) && displayCategory && post.cat && (
        <TagName
          href={!isEditing ? post.catLink : undefined}
          className={style.cat}
        >
          {he.decode(post.cat)}
        </TagName>
      )}
    </div>
  );
};

export default Post;
